<template>
  <div 
    class="task-card"
    :class="{ selected: isSelected, 'status-done': task.status === 'done' }"
    :draggable="true"
    @dragstart="$emit('dragstart', $event, task)"
    @click="$emit('click', task)"
  >
    <div class="card-header">
      <span class="priority-dot" :class="priorityClass"></span>
      <span class="task-title">{{ task.title }}</span>
      <button 
        v-if="task.status !== 'done'"
        class="btn-delete"
        title="delete"
        aria-label="delete"
        @click.stop="$emit('delete', task)"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M9 3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h5a1 1 0 1 1 0 2h-1v14a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3V6H4a1 1 0 1 1 0-2h5V3zm2 1h2V4h-2V4zM7 6v14a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6H7zm3 3a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V10a1 1 0 0 1 1-1zm4 0a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V10a1 1 0 0 1 1-1z"/>
        </svg>
      </button>
      <button 
        v-if="showMenu"
        class="btn-menu" 
        @click.stop="toggleMenu"
      >
        ⋯
      </button>
    </div>

    <div class="card-meta">
      <template v-if="assigneeBadges.length">
        <span 
          v-for="(a, idx) in assigneeBadges" 
          :key="'assignee-'+idx" 
          class="assignee-badge" 
          :title="a.title"
        >
          {{ a.initials }}
        </span>
        <span v-if="assigneeExtraCount > 0" class="assignee-extra">+{{ assigneeExtraCount }}</span>
      </template>
      <span v-if="task.due_date" class="due-date" :class="dueDateClass">{{ formattedDueDate }}</span>
      <span v-if="hasAgent" class="agent-icon">⚡</span>
      <span v-if="chatCount > 0" class="chat-count">{{ chatCount }}</span>
      <span v-if="goalLabel" class="goal-badge">🎯 {{ goalLabel }}</span>
      <span v-if="tokenTotal !== null" class="token-badge">⨯ {{ tokenTotal }}</span>
    </div>

    <!-- Quick actions menu -->
    <div v-if="menuOpen" class="menu-dropdown" @click.stop>
      <button @click="$emit('edit', task)">Edit</button>
      <button @click="$emit('duplicate', task)">Duplicate</button>
      <button @click="$emit('delete', task)" class="danger">Delete</button>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'TaskCard',
  props: {
    task: {
      type: Object,
      required: true
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    goalLabel: {
      type: [String, null],
      default: null
    },
    tokenTotal: {
      type: [Number, String, null],
      default: null
    }
  },
  emits: ['click', 'dragstart', 'edit', 'duplicate', 'delete'],
  setup(props) {
    const showMenu = ref(false)
    const menuOpen = ref(false)

    const MAX_ASSIGNEES_DISPLAY = 3

    const initialsFor = (nameOrEmail) => {
      if (!nameOrEmail) return ''
      const base = String(nameOrEmail)
      const parts = base.includes('@') ? base.split('@')[0].split(/[._-]/) : base.split(/\s+/)
      const letters = parts.filter(Boolean).map(p => p[0].toUpperCase())
      return (letters[0] || '?') + (letters[1] || '')
    }

    const priorityClass = computed(() => {
      if (props.task.status === 'done') return 'done'
      return props.task.priority || 'default'
    })

    // Build assignee badges from assignees_details (multiple users)
    const assigneeBadges = computed(() => {
      const details = props.task?.assignees_details
      if (Array.isArray(details) && details.length > 0) {
        const mapped = details.map(d => {
          const title = (d?.name && String(d.name).trim()) || (d?.email && String(d.email).trim()) || ''
          const initials = initialsFor(title || d?.email || '')
          return { title, initials }
        }).filter(a => a.initials)
        return mapped.slice(0, MAX_ASSIGNEES_DISPLAY)
      }
      // Fallback to single-name displays if details not present
      const names = Array.isArray(props.task?.assignees_names) ? props.task.assignees_names : []
      if (names.length > 0) {
        return names.slice(0, MAX_ASSIGNEES_DISPLAY).map(n => ({ title: n, initials: initialsFor(n) }))
      }
      const nm = props.task.assignee_name || ''
      return nm ? [{ title: nm, initials: initialsFor(nm) }] : []
    })

    const assigneeExtraCount = computed(() => {
      const details = props.task?.assignees_details
      const total = Array.isArray(details) && details.length > 0 ? details.length : (Array.isArray(props.task?.assignees_names) ? props.task.assignees_names.length : (props.task?.assignee_name ? 1 : 0))
      return Math.max(0, total - MAX_ASSIGNEES_DISPLAY)
    })

    const hasAgent = computed(() => {
      return props.task.agent_ids && props.task.agent_ids.length > 0
    })

    const chatCount = computed(() => {
      return props.task.chat_count || 0
    })

    const formattedDueDate = computed(() => {
      if (!props.task.due_date) return null
      const date = new Date(props.task.due_date)
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      
      // Reset hours for comparison
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      const tomorrowOnly = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate())
      
      if (dateOnly < todayOnly) return 'Overdue'
      if (dateOnly.getTime() === todayOnly.getTime()) return 'Today'
      if (dateOnly.getTime() === tomorrowOnly.getTime()) return 'Tomorrow'
      
      // Format as "Dec 15"
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${months[date.getMonth()]} ${date.getDate()}`
    })

    const dueDateClass = computed(() => {
      if (!props.task.due_date) return ''
      const date = new Date(props.task.due_date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      if (date < today) return 'overdue'
      if (date.toDateString() === today.toDateString()) return 'today'
      return 'upcoming'
    })

    const toggleMenu = () => {
      menuOpen.value = !menuOpen.value
    }

    return {
      showMenu,
      menuOpen,
      priorityClass,
      assigneeBadges,
      assigneeExtraCount,
      hasAgent,
      chatCount,
      formattedDueDate,
      dueDateClass,
      toggleMenu
    }
  }
}
</script>

<style scoped>
.task-card {
  position: relative;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 8px;
}

.task-card:hover {
  border-color: #cbd5e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.task-card:hover .btn-menu {
  opacity: 1;
}

.task-card:hover .btn-delete {
  opacity: 1;
}

.task-card.selected {
  border-color: #667eea;
  border-width: 2px;
  background: #f8f9ff;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.task-card.status-done {
  opacity: 0.6;
}

.task-card.status-done .task-title {
  text-decoration: line-through;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.priority-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.priority-dot.high {
  background: #ef4444;
}

.priority-dot.medium {
  background: #f97316;
}

.priority-dot.low,
.priority-dot.done {
  background: #10b981;
}

.priority-dot.default {
  background: #9ca3af;
}

.task-title {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  color: #37352f;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-menu {
  background: none;
  border: none;
  font-size: 16px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.btn-menu:hover {
  color: #6c757d;
}

.btn-delete {
  background: none;
  border: none;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: 0;
  transition: opacity 0.15s, transform 0.1s;
  flex-shrink: 0;
  line-height: 1;
}

.btn-delete svg {
  width: 16px;
  height: 16px;
  fill: #ef4444; /* red */
}

.btn-delete:hover svg {
  fill: #dc2626; /* darker red on hover */
}

.btn-delete:active {
  transform: scale(0.96);
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6c757d;
  flex-wrap: wrap;
}

.assignee-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #111827;
  color: #fff;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  border: 1px solid #e5e7eb;
}
.assignee-extra { font-size: 11px; color: #6b7280; }

.goal-badge {
  background: #eef2ff;
  color: #37352f;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
}

.token-badge {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
}

.due-date {
  font-size: 12px;
}

.due-date.overdue {
  color: #ef4444;
  font-weight: 600;
}

.due-date.today {
  color: #f97316;
  font-weight: 500;
}

.due-date.upcoming {
  color: #6c757d;
}

.agent-icon {
  font-size: 14px;
}

.chat-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 9px;
  font-size: 11px;
  font-weight: 500;
  color: #6c757d;
}

.menu-dropdown {
  position: absolute;
  top: 32px;
  right: 8px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
  min-width: 120px;
  overflow: hidden;
}

.menu-dropdown button {
  display: block;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  text-align: left;
  font-size: 13px;
  color: #37352f;
  cursor: pointer;
  transition: background 0.15s;
}

.menu-dropdown button:hover {
  background: rgba(0, 0, 0, 0.05);
}

.menu-dropdown button.danger {
  color: #ef4444;
}

.menu-dropdown button.danger:hover {
  background: rgba(239, 68, 68, 0.1);
}
</style>
